import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useApiEntity } from '../../hooks/use-api-entity'
import { useModal } from '../../hooks/use-modal'
import { useAuth } from '../../hooks/use-auth'
import { api } from '../../services/api'
import {
  PageHeader,
  FormField,
  Alert,
  Modal,
  ConfirmDialog,
  btnPrimary,
  btnSecondary,
  btnDanger,
  card,
  input,
  colors,
} from '../../components/ui'

interface UserForm {
  email: string
  firstName: string
  lastName: string
  phone: string
  role: string
  status: string
  password: string
}

const defaults: UserForm = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  role: 'viewer',
  status: 'active',
  password: '',
}

export function UserEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const { data, isNew, loading, saving, error, success, save, cancel, update, setError } = useApiEntity<UserForm>({
    path: '/users',
    listPath: '/settings/users',
    defaultValue: defaults,
  })

  const resetModal = useModal()
  const archiveModal = useModal()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resetError, setResetError] = useState<string | null>(null)
  const [resetSuccess, setResetSuccess] = useState<string | null>(null)
  const [resetting, setResetting] = useState(false)
  const [archiving, setArchiving] = useState(false)

  const handleResetPassword = async () => {
    setResetError(null)
    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match')
      return
    }
    setResetting(true)
    try {
      await api.post(`/users/${id}/reset-password`, { newPassword })
      setResetSuccess('Password reset successfully')
      resetModal.close()
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Failed to reset password')
    } finally {
      setResetting(false)
    }
  }

  const handleArchive = async () => {
    setArchiving(true)
    try {
      await api.delete(`/users/${id}`)
      navigate('/settings/users')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive user')
      archiveModal.close()
    } finally {
      setArchiving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  return (
    <div>
      <PageHeader
        title={isNew ? 'New User' : `${data.firstName} ${data.lastName}`}
        backTo="/settings/users"
        backLabel="Back to Users"
        actions={
          <>
            {!isNew && (
              <button onClick={resetModal.open} style={btnPrimary}>
                Reset Password
              </button>
            )}
            {!isNew && (
              <button onClick={archiveModal.open} style={btnDanger}>
                Archive
              </button>
            )}
            <button onClick={cancel} style={btnSecondary}>
              Cancel
            </button>
            <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      />
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}
      {resetSuccess && <Alert type="success" message={resetSuccess} />}

      <div style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <FormField
          name="email"
          label="Email"
          required
          type="email"
          value={data.email}
          onChange={(v) => update('email', v)}
          disabled={!isNew}
        />
        <FormField
          name="firstName"
          label="First Name"
          required
          value={data.firstName}
          onChange={(v) => update('firstName', v)}
        />
        <FormField
          name="lastName"
          label="Last Name"
          required
          value={data.lastName}
          onChange={(v) => update('lastName', v)}
        />
        <FormField name="phone" label="Phone" value={data.phone} onChange={(v) => update('phone', v)} />
        <FormField name="role" label="Role" required>
          <select style={input} value={data.role} onChange={(e) => update('role', e.target.value)}>
            {currentUser?.role === 'medflow_admin' && <option value="medflow_admin">MedFlow Admin</option>}
            {currentUser?.role === 'medflow_admin' && <option value="customer_admin">Customer Admin</option>}
            <option value="admin">Admin</option>
            <option value="tech">Tech</option>
            <option value="lab_tech">Lab Tech</option>
            <option value="provider">Provider</option>
            <option value="viewer">Viewer</option>
          </select>
        </FormField>
        <FormField name="status" label="Status">
          <select style={input} value={data.status} onChange={(e) => update('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </FormField>
        {isNew && (
          <FormField
            name="password"
            label="Password"
            required
            type="password"
            value={data.password}
            onChange={(v) => update('password', v)}
          />
        )}
      </div>

      <Modal isOpen={resetModal.isOpen} onClose={resetModal.close} title="Reset Password">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {resetError && <Alert type="error" message={resetError} />}
          <FormField
            name="newPassword"
            label="New Password"
            required
            type="password"
            value={newPassword}
            onChange={setNewPassword}
            placeholder="Minimum 8 characters"
          />
          <FormField
            name="confirmPassword"
            label="Confirm Password"
            required
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button style={btnSecondary} onClick={resetModal.close}>
              Cancel
            </button>
            <button style={btnPrimary} onClick={handleResetPassword} disabled={resetting}>
              {resetting ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive User"
        message={`Are you sure you want to archive ${data.firstName} ${data.lastName}? They will no longer be able to log in.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  )
}
